// Import necessary React utilities and custom modules
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';              
import { useCommon } from '../context/CommonContext';      
import Loading from "./Loading";                             
import LanguageSwitcher from './LanguageSwitcher';         
import { useLanguage } from '../context/LanguageProvider';
import { defaultTexts, checkLogIn as checkLogInUtil, updateTexts as updateTextsUtil } from '../utils/surveyUtils';
import { enterFullscreen } from '../utils/fullscreenAndNavigate';


// Main functional component for survey ID entry screen
const SurveyEntry = () => {
  console.log("------------------surveyEntry rendered----------------------");

  // Destructure shared context values and UI text references
  const { surveyId, setSurveyId, setSampleMode, vasStatus,
          welcomeText, intermissionText, implicitExplicitText, 
          consumptionText, surveyEndText, nextButtonText, 
          kioskTimeoutText, inputUserIDText, continueButtonText, 
          beginButtonText, deviceCount, intermissionNumber, intermissionTime } = useCommon();

  // Destructure translation function from language context
  const { translation } = useLanguage();
  
  // Used for navigation after successful login
  const navigate = useNavigate();

  // Local state for error message and disabling input during login
  const [errorMessage, setErrorMessage] = useState('');
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  // Reference to store language-specific text fetched from API
  const languagesRef = useRef([]);

  // Default UI text values before language data is loaded
  const texts = {
    ...defaultTexts
  };

  // Handles clicks on numeric keypad and special keys (←, enter)
  const handleButtonClick = (value) => {

    const currentId = surveyId || '';
    if (isInputDisabled) return; // Prevent input if disabled
    
    // Add numeric input if under length limit
    if (currentId.length < 10) {
      if (value !== '←' && value !== 'enter') {
        setSurveyId(currentId + value);
        setErrorMessage("");
      }
    }

    // Backspace: remove last character
    if (value === '←') {
      setSurveyId(currentId.slice(0, -1));
      setErrorMessage("");
    }

    // Enter: validate and attempt login
    if (value === 'enter') {
      enterFullscreen();
      if (!surveyId) {
        setErrorMessage(translation("Survey_surveyIDPrompt"));
      } else {
        setIsInputDisabled(true);
         checkLogInUtil({
          surveyId,
          setSampleMode,
          intermissionNumber,
          intermissionTime,
          vasAlert,
          translation,
          setIsInputDisabled,
          setErrorMessage,
          languagesRef,
        });
      }
    }
  };

  // Handles special logic for "VAS" sample mode; confirms eligibility before proceeding
  const vasAlert = (mode) => {
    if (mode === "VAS" && (vasStatus.current !== "websocket" || deviceCount !== 1)) {
      const userChoice = window.confirm(translation("Survey_invalidVASMessage"));
      if (userChoice) {
        updateTextsUtil({
          languagesRef,
          texts,
          welcomeText,
          implicitExplicitText,
          intermissionText,
          consumptionText,
          surveyEndText,
          beginButtonText,
          continueButtonText,
          inputUserIDText,
          kioskTimeoutText,
          nextButtonText,
        });
        navigate('/Panelist');
      } else {
        setIsInputDisabled(false);
        navigate('/');
      }
    } else {
      updateTextsUtil({
          languagesRef,
          texts,
          welcomeText,
          implicitExplicitText,
          intermissionText,
          consumptionText,
          surveyEndText,
          beginButtonText,
          continueButtonText,
          inputUserIDText,
          kioskTimeoutText,
          nextButtonText,
        });
      navigate('/Panelist');
    }
  };

  // Handles manual text input in the survey ID field
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow only numeric input and only when enabled
    if (/^\d*$/.test(value) && !isInputDisabled) {
      setSurveyId(value);
      setErrorMessage("");
    }
  };

  // Submits on pressing Enter key in text input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleButtonClick('enter');
    }
  };

  // Show loading indicator when input is disabled (e.g., during login check)
  if (isInputDisabled) {
    return <Loading />;
  }

  // Render survey ID entry screen with keypad, language switcher, and error message
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className='flex flex-col items-center justify-center relative'>
        
        {/* Language switching component */}
        <LanguageSwitcher className="border-[1px] border-solid border-neutral-800 rounded-lg"/>
        
        {/* Survey ID prompt */}
        <div className="mb-4 mt-4">
          <h1 className="text-center font-bold text-5xl mb-4">
            <label htmlFor='surveyIdlogin'>{translation("Survey_surveyIDPrompt")}</label> 
          </h1>
        </div>
        
        {/* Input field and keypad */}
        <div className="bg-black p-8 rounded-md">
          <div className="border-t-8 border-l-8 border-r-8 border-b-4 border-black ">
            <input
              type="text"
              id="surveyIdlogin"
              value={surveyId || ''}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isInputDisabled}
              className="w-96 h-20 p-2 border-4 border-gray-600 rounded-lg outline-black text-center text-5xl font-sans font-medium text-black"
            />
          </div>

          {/* Numeric keypad grid */}
          <div className="grid grid-cols-3 gap-4 mt-5 justify-items-center">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '←', '0', 'enter'].map((value, index) => (
              <div
                key={index}
                onClick={() => handleButtonClick(value)}
                className="flex items-center justify-center w-16 h-16 border-3 border-white bg-gray-200 rounded-md cursor-pointer shadow-lg"
              >
                {/* Show correct label for special keys */}
                <span className="text-2xl font-medium">
                  {value === '←' ? '←' : value === 'enter' ? 'enter' : value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Error message display */}
        {errorMessage && (
          <span className="absolute -bottom-10 text-red-600 text-2xl font-bold mt-4">
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  );
};

export default SurveyEntry;
